import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api, fmt } from '../api';
import { Empty, ErrorNote, Loading, PageHead, Stat, StatusChip, useLoad } from '../components/ui';

interface Detail {
  conversation: {
    id: string; channel: string; status: string; agent_name: string; phone: string; started_at: number; answered_at: number | null; ended_at: number | null;
    duration_ms: number; end_reason: string; turns: number; interruptions: number; cost_inr: number; llm: string; tts: string; stt: string; live: boolean;
    usage: { sttMs?: number; llmIn?: number; llmOut?: number; ttsChars?: number; ttsMs?: number };
    metrics: { connectMs?: number; turns?: { turn: number; firstPartialMs?: number; finalizeMs?: number; firstTokenMs?: number; firstAudioMs?: number }[]; bargeIns?: { detectMs: number; stopMs?: number }[] };
  };
  messages: { role: string; text: string; interrupted: number; at_ms: number }[];
}

export default function ConversationView({ owner }: { owner: boolean }) {
  const { id } = useParams();
  const { data, error, loading } = useLoad(() => api.get<Detail>(`/api/conversations/${id}`), [id]);
  if (loading && !data) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;
  const c = data.conversation;
  const turns = c.metrics.turns ?? [];
  const avg = (k: 'firstAudioMs' | 'firstTokenMs' | 'finalizeMs') => {
    const v = turns.map((t) => t[k]).filter((x): x is number => typeof x === 'number');
    return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
  };

  const whole = turns.filter((t) => typeof t.finalizeMs === 'number' && typeof t.firstAudioMs === 'number').map((t) => t.finalizeMs! + t.firstAudioMs!);
  const total = whole.length ? Math.round(whole.reduce((a, b) => a + b, 0) / whole.length) : null;

  return (
    <>
      <Link to={owner ? '/owner/conversations' : '/history'} className="small" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <ArrowLeft size={14} aria-hidden="true" /> Back
      </Link>
      <PageHead
        title={`${c.agent_name} · ${fmt.channel(c.channel)}`}
        sub={<>{fmt.date(c.started_at)}{c.phone ? <> · <span className="mono">{c.phone}</span></> : null} · <StatusChip status={c.live ? 'live' : c.status} /></>}
      />
      <div className="stats">
        <Stat label="Length" value={fmt.duration(c.duration_ms)} />
        <Stat label="Turns" value={c.turns} hint={`${c.interruptions} interruptions`} />
        {/* Two clocks (the owner's question, 2026-10-15): the whole wait the caller feels, and the part after the
            end of their turn was understood. The difference is the end-of-turn wait. */}
        <Stat label="Avg wait for a reply" value={fmt.ms(total)} hint="you stop talking → the agent's first sound" />
        <Stat label="Avg reply audio" value={fmt.ms(avg('firstAudioMs'))} hint="after your turn was understood" />
        {owner ? <Stat label="AI runtime cost" value={fmt.inr(c.cost_inr, 3)} hint="estimate · telephony excluded" /> : null}
      </div>

      <div className="grid-2" style={{ gridTemplateColumns: owner ? 'minmax(0, 1.4fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', alignItems: 'start' }}>
        <section className="card">
          <h2 className="mt0">Transcript</h2>
          {data.messages.length === 0 ? <Empty>No words were exchanged.</Empty> : (
            <div className="transcript" style={{ maxHeight: 'none' }}>
              {data.messages.map((m, i) => (
                <div key={i} className={`bubble ${m.role} ${m.interrupted ? 'cut' : ''}`}>
                  <span className="meta">{m.role === 'user' ? 'Caller' : c.agent_name.split('·')[0]} · {fmt.duration(m.at_ms)}{m.interrupted ? ' · interrupted' : ''}</span>
                  {m.text}
                </div>
              ))}
            </div>
          )}
        </section>
        {owner ? (
          <section className="card">
            <h2 className="mt0">Pipeline</h2>
            <dl className="kv">
              <dt>Speech</dt><dd className="mono">{c.stt}</dd>
              <dt>Brain</dt><dd className="mono">{c.llm}</dd>
              <dt>Voice</dt><dd className="mono">{c.tts}</dd>
              <dt>Ended</dt><dd>{c.end_reason || '—'}</dd>
              <dt>Connect</dt><dd>{fmt.ms(c.metrics.connectMs)}</dd>
              <dt>Usage</dt><dd className="small">{Math.round((c.usage.sttMs ?? 0) / 1000)}s heard · {c.usage.llmIn ?? 0}/{c.usage.llmOut ?? 0} tokens · {c.usage.ttsChars ?? 0} chars spoken</dd>
            </dl>
            {turns.length ? (
              <>
                <h3 style={{ marginTop: 18 }}>Per-turn latency</h3>
                <div className="tablewrap">
                  <table style={{ minWidth: 0 }}>
                    <thead><tr><th>Turn</th><th className="num">Finalize</th><th className="num">Token</th><th className="num">Audio</th></tr></thead>
                    <tbody>
                      {turns.map((t) => (
                        <tr key={t.turn}><td>{t.turn}</td><td className="num">{fmt.ms(t.finalizeMs)}</td><td className="num">{fmt.ms(t.firstTokenMs)}</td><td className="num">{fmt.ms(t.firstAudioMs)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
            {c.metrics.bargeIns?.length ? (
              <p className="small muted" style={{ marginBottom: 0 }}>
                Barge-ins: {c.metrics.bargeIns.map((b) => `${b.detectMs} ms detect${b.stopMs !== undefined ? ` / ${b.stopMs} ms stop` : ''}`).join(', ')}
              </p>
            ) : null}
          </section>
        ) : null}
      </div>
    </>
  );
}
